// Client-side "verify against the messages" search. The browser already holds
// the canonical corpus.csv in memory (the same File it uploads to classify), so
// we can surface real message excerpts for a nickname WITHOUT any server call —
// strictly more private than the classify upload, and it fits the
// corpus-stays-in-the-browser principle. This is the cloud's getExcerpts seam
// that mirrors desktop's searchCorpus; desktop queries an indexed SQLite corpus,
// here we linear-scan the parsed rows (fine at corpus scale, one-off on click).
import { parseCsvText } from "../adapters/generic_csv";
import type { NameExcerpt } from "@app/components/PartiesChildrenEditor";

/** Longest body we show in a popover line — keep it tidy. */
const MAX_TEXT = 280;

/** Pure: up to `limit` excerpts whose body mentions `term` (case-insensitive,
 *  word-boundary where the term is a plain word so "Mia" doesn't hit "Miami"). */
export function findExcerpts(
  rows: { timestamp?: string; sender?: string; body?: string }[],
  term: string,
  limit = 3,
): NameExcerpt[] {
  const t = term.trim();
  if (!t) return [];
  const safe = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Word boundary when the term is alphanumeric; otherwise a plain substring.
  const re = /^[\p{L}\p{N}]+$/u.test(t)
    ? new RegExp(`\\b${safe}\\b`, "iu")
    : new RegExp(safe, "iu");
  const out: NameExcerpt[] = [];
  for (const r of rows) {
    const body = r.body ?? "";
    if (!body || !re.test(body)) continue;
    out.push({
      sender: r.sender ?? "",
      date: r.timestamp ?? "",
      text: body.length > MAX_TEXT ? body.slice(0, MAX_TEXT - 1) + "…" : body,
    });
    if (out.length >= limit) break;
  }
  return out;
}

/** Build a getExcerpts(term) bound to one corpus File. Parses the CSV once
 *  (lazily, cached) so repeated nickname checks don't re-parse. Returns a
 *  function matching the editor's `getExcerpts` prop. */
export function makeExcerptSearcher(
  file: File,
): (term: string) => Promise<NameExcerpt[]> {
  let rowsPromise: Promise<{ timestamp?: string; sender?: string; body?: string }[]> | null =
    null;
  const load = () => {
    if (!rowsPromise) {
      rowsPromise = file
        .text()
        .then((text) => parseCsvText(text).rows)
        .catch(() => []);
    }
    return rowsPromise;
  };
  return async (term: string) => findExcerpts(await load(), term);
}
